import { manifestApi } from "#/api/manifestApi";
import { showSuccessNotification } from "#/components/notifications";
import type { HawbRow } from "#/types/manifest";
import { formatHawbLabel } from "#/utils/manifestUtils";
import { logger } from "#/utils/logger";
import { Autocomplete, Button, Modal, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useDebouncedValue } from "@mantine/hooks";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import HawbDetailsFields from "./HawbDetailsFields";
import { hawbDetailsValidation, toHawbPayload, type HawbDetailsValues } from "./hawbForm";

interface CreateHawbModalProps {
  opened: boolean;
  onClose: () => void;
  onCreated: () => void;
}

interface CreateValues extends HawbDetailsValues {
  awb_number: string;
  hawb_number: string;
}

const INITIAL_VALUES: CreateValues = {
  awb_number: "",
  hawb_number: "",
  arrival_date: null,
  origin_station: "",
  destination: "",
  responsible_person: "",
  packages: "",
  weight_kg: "",
  storage_fee_paid: false,
  blocked: false,
  has_pick_list: false,
};

/** "-" in the file means "no HAWB"; the form treats it the same way. */
const normalizeHawb = (value: string): string => (value.trim() === "-" ? "" : value.trim());

/**
 * Manual consignment creation. Saving creates the MAWB if new, the HAWB and one
 * untagged item per package. A duplicate AWB + HAWB is blocked with a link to
 * the existing consignment.
 */
const CreateHawbModal = ({ opened, onClose, onCreated }: CreateHawbModalProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [duplicate, setDuplicate] = useState<HawbRow | null>(null);

  const form = useForm<CreateValues>({
    initialValues: INITIAL_VALUES,
    validate: {
      awb_number: (value) => {
        const trimmed = value.trim();
        if (!trimmed) return "manifests.form.awbRequired";
        return trimmed.length > 32 ? "manifests.form.awbTooLong" : null;
      },
      hawb_number: (value) => (normalizeHawb(value).length > 64 ? "manifests.form.hawbTooLong" : null),
      ...hawbDetailsValidation,
    },
  });

  const [debouncedAwb] = useDebouncedValue(form.values.awb_number.trim(), 300);
  const { data: suggestions = [] } = useQuery({
    queryKey: ["mawb-suggest", debouncedAwb],
    queryFn: async () => (await manifestApi.suggestMawb(debouncedAwb)).data,
    enabled: opened,
  });

  const handleClose = () => {
    form.reset();
    setDuplicate(null);
    onClose();
  };

  /** The API's 409 does not carry the existing id, so look it up by number. */
  const findExisting = async (awb: string, hawb: string): Promise<HawbRow | null> => {
    const response = await manifestApi.getAll({ page: 1, limit: 50, search: hawb || awb });
    return (
      response.data.data.find((row) => row.awb_number === awb && row.hawb_number === hawb) ?? null
    );
  };

  const createMutation = useMutation({
    mutationFn: (values: CreateValues) =>
      manifestApi.create({
        awb_number: values.awb_number.trim(),
        hawb_number: normalizeHawb(values.hawb_number),
        ...toHawbPayload(values),
      }),
    onSuccess: (response) => {
      showSuccessNotification({
        message: t("manifests.create.success", {
          label: formatHawbLabel(response.data),
          count: response.data.packages,
        }),
      });
      onCreated();
      handleClose();
    },
    onError: async (error, values) => {
      logger.error("create consignment error:", error);
      try {
        setDuplicate(await findExisting(values.awb_number.trim(), normalizeHawb(values.hawb_number)));
      } catch (lookupError) {
        logger.error("duplicate lookup error:", lookupError);
      }
    },
  });

  const errorOf = (field: "awb_number" | "hawb_number") => {
    const error = form.errors[field];
    return typeof error === "string" ? t(error) : undefined;
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      centered
      size="lg"
      title={t("manifests.create.title")}
      closeOnClickOutside={false}
      classNames={{ title: "font-bold" }}
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={form.onSubmit((values) => {
          setDuplicate(null);
          createMutation.mutate(values);
        })}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Autocomplete
            label={t("manifests.columns.awb")}
            placeholder={t("manifests.form.awbPlaceholder")}
            description={t("manifests.form.awbDescription")}
            required
            data={suggestions.map((s) => s.awb_number)}
            {...form.getInputProps("awb_number")}
            error={errorOf("awb_number")}
          />
          <TextInput
            label={t("manifests.columns.hawb")}
            placeholder={t("manifests.form.hawbPlaceholder")}
            description={t("manifests.form.hawbDescription")}
            {...form.getInputProps("hawb_number")}
            error={errorOf("hawb_number")}
          />
        </div>
        <HawbDetailsFields form={form} />

        {duplicate && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 flex items-center justify-between gap-2">
            <span>{t("manifests.create.duplicate", { label: formatHawbLabel(duplicate) })}</span>
            <Button
              size="xs"
              variant="outline"
              color="red"
              onClick={() => void navigate(`/fleet/shipment-manifests/${duplicate.id}`)}
            >
              {t("manifests.create.openExisting")}
            </Button>
          </div>
        )}

        <div className="w-full grid grid-cols-[auto_1fr] items-center gap-2">
          <Button size="sm" color="black" variant="outline" onClick={handleClose}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" size="sm" color="black" loading={createMutation.isPending}>
            {t("manifests.create.submit")}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default CreateHawbModal;
